import Button from '@/components/Buttons/Button';
import { VIDEO_TILE_WIDTH } from '@/components/VideoTile';
import { AvatarTile } from '@/pages/avatar';
import { SectionTitle } from '@/pages/home/components/Common';
import { Presenter } from '@d-id/types';
import styled from '@emotion/styled';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CreateAvatarTile } from './CreateAvatarTile';

const ROWS_COUNT = 2;

const GridContainer = styled.div`
    display: grid;
`;

const LibraryContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

// todo - should be replaced to new white button
const ShowAllButton = styled(Button, { shouldForwardProp: prop => prop !== 'white' })<{ white?: boolean }>`
    background-color: ${({ theme, white }) => white && theme.colors.white};
    color: ${({ theme, white }) => white && theme.colors.black[800]};
    border-color: ${({ theme, white }) => white && theme.colors.black[300]};

    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
    padding: 14px 24px;
    gap: 8px;
    height: 48px;
    white-space: nowrap;
    width: min-content;
    margin: 0 auto;

    :hover {
        border-color: ${({ theme, white }) => white && theme.colors.black[300]} !important;
    }
`;

const AvatarsContainer = styled.div<{ rows: number; seeAll: boolean }>`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${VIDEO_TILE_WIDTH}px, 1fr));
    grid-row: 1; /* Ensure each row takes up equal height */
    grid-gap: 24px;
    overflow: hidden;

    grid-template-rows: ${({ seeAll, rows }) => (seeAll ? 'unset' : `repeat(${rows}, 1fr)`)};
    grid-auto-rows: ${({ seeAll }) => (seeAll ? 'unset' : 0)}; /* set height to 0 for autogenerated grid rows */
`;

interface Props {
    avatars?: Presenter[];
}

export const UserLibrary = ({ avatars = [] }: Props) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [limit, setLimit] = useState<number>(1);
    const [seeAll, setSeeAll] = useState<boolean>(false);

    const elements = useMemo(() => [...avatars].splice(0, limit), [avatars, limit]);
    const showSeeAll = avatars.length > elements.length;
    const showRowsCount = elements.length <= (limit - 1) / 2 ? 1 : 2;

    const calculateLimit = () => {
        const width = ref.current?.offsetWidth || 0;
        const gap = Math.round((24 * limit) / (limit + 1)); // 24 is the gap between tiles
        const newLimit = Math.floor(width / (VIDEO_TILE_WIDTH + gap)); // 24 is the gap between tiles

        const videosWidth = VIDEO_TILE_WIDTH * newLimit;
        const gapWidth = 24 * (newLimit - 1);
        const neededWidth = videosWidth + gapWidth;

        if (width < neededWidth) {
            setLimit(Math.max(newLimit - 1, 1) * ROWS_COUNT - 1); // -1 because of UploadAvatarTile
        } else {
            setLimit(Math.max(newLimit, 1) * ROWS_COUNT - 1); // -1 because of UploadAvatarTile
        }
    };

    useEffect(() => {
        window.addEventListener('resize', calculateLimit);

        return () => {
            window.removeEventListener('resize', calculateLimit);
        };
    }, []);

    useEffect(() => {
        if (seeAll) {
            handleSeeAll();
        } else {
            calculateLimit();
        }
    }, [avatars.length]);

    const handleSeeAll = () => {
        setLimit(avatars?.length);
        setSeeAll(true);
    };

    return (
        <>
            <LibraryContainer data-testid="user-library-container">
                <SectionTitle>My Avatars</SectionTitle>
                <GridContainer>
                    <AvatarsContainer ref={ref} seeAll={seeAll} rows={showRowsCount}>
                        <CreateAvatarTile />
                        {elements.map(a => (
                            <AvatarTile key={a.id} avatar={a} editable isAiGenerated={!!a.is_aiGenerated} />
                        ))}
                    </AvatarsContainer>
                </GridContainer>
            </LibraryContainer>
            {showSeeAll && !seeAll && (
                <ShowAllButton color="secondary" variant="outlined" white onClick={handleSeeAll}>
                    Show all
                </ShowAllButton>
            )}
        </>
    );
};
